import { defaultMemoize } from 'reselect';
import { ThemeParameters, RuntimeThemeParameters } from '../../types';
import { increaseLumaDelta } from '../util';
import c from '../colors';
import { graphWallFill } from './';

interface GraphDataStylePattern {
  markerSymbol: string;
  lineStyle: string;
  fillPattern: string;
  color: string;
  fillColor: string;
  altColor: string;
  markerColor: string;
  lineColor: string;
  textColor: string;
  markerSize: number;
  lineThickness: number;
}

interface GraphDataStyle {
  styleDefault: (
    theme: ThemeParameters,
    parameters: RuntimeThemeParameters
  ) => GraphDataStylePattern;
  style1: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style2: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style3: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style4: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style5: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style6: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style7: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style8: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style9: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style10: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
  style11: (theme: ThemeParameters, parameters: RuntimeThemeParameters) => GraphDataStylePattern;
}

// functions styleDefault - style8 are used to define multiple other tokens. For that reason we're using
// defaultMemoize to increase performance. style9 - style11 are used only in edge cases.
export const graphDataStyle: GraphDataStyle = {
  styleDefault: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const common = {
      markerSymbol: 'circlefilled',
      lineStyle: 'solid',
      fillPattern: 'L1',
      ...deriveDataStyle(theme, parameters, c.colorGraph1(theme)),
    };
    switch (theme.baseThemeName) {
      case 'dark': {
        return {
          ...common,
          markerSize: 11,
          lineThickness: 3,
        };
      }
      case 'high-contrast': {
        return {
          ...common,
          markerSize: 16,
          lineThickness: 5,
        };
      }
      default: {
        return {
          ...common,
          markerSize: 11,
          lineThickness: 3,
        };
      }
    }
  }),
  style1: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    return graphDataStyle.styleDefault(theme, parameters);
  }),
  style2: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    return {
      ...deriveDataStyle(theme, parameters, c.colorGraph2(theme)),
      markerSize: styleDefault.markerSize,
      markerSymbol: 'squarefilled',
      lineStyle: 'mediumdash',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'X1',
    };
  }),
  style3: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    return {
      ...deriveDataStyle(theme, parameters, c.colorGraph3(theme)),
      markerSize: styleDefault.markerSize,
      markerSymbol: 'trianglefilled',
      lineStyle: 'mediumdashshortdash',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'R4',
    };
  }),
  style4: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    return {
      ...deriveDataStyle(theme, parameters, c.colorGraph4(theme)),
      markerSize: styleDefault.markerSize,
      markerSymbol: 'diamondfilled',
      lineStyle: 'longdash',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'X3',
    };
  }),
  style5: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    return {
      ...deriveDataStyle(theme, parameters, c.colorGraph5(theme)),
      markerSize: styleDefault.markerSize,
      markerSymbol: 'starfilled',
      lineStyle: 'dashdashdot',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'R3',
    };
  }),
  style6: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    return {
      ...deriveDataStyle(theme, parameters, c.colorGraph6(theme)),
      markerSize: styleDefault.markerSize,
      markerSymbol: 'triangledownfilled',
      lineStyle: 'longdashshortdash',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'L5',
    };
  }),
  style7: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    const common = {
      markerSize: styleDefault.markerSize,
      markerSymbol: 'homedownfilled',
      lineStyle: 'dashdotdot',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'R1',
    };
    switch (theme.baseThemeName) {
      case 'dark': {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph7(theme)),
          ...common,
        };
      }
      case 'high-contrast': {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph7(theme)),
          ...common,
        };
      }
      default: {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph7(theme)),
          ...common,
        };
      }
    }
  }),
  style8: defaultMemoize((theme: ThemeParameters, parameters: RuntimeThemeParameters) => {
    const styleDefault = graphDataStyle.styleDefault(theme, parameters);
    const common = {
      markerSize: styleDefault.markerSize,
      markerSymbol: styleDefault.markerSymbol,
      lineStyle: 'dash',
      lineThickness: styleDefault.lineThickness,
      fillPattern: 'X5',
    };
    switch (theme.baseThemeName) {
      case 'dark': {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph8(theme)),
          ...common,
        };
      }
      case 'high-contrast': {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph8(theme)),
          ...common,
        };
      }
      default: {
        return {
          ...deriveDataStyle(theme, parameters, c.colorGraph8(theme)),
          ...common,
        };
      }
    }
  }),
  style9(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return {
      ...graphDataStyle.styleDefault(theme, parameters),
      lineStyle: 'shortdashdot',
      fillPattern: 'L4',
    };
  },
  style10(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return {
      ...graphDataStyle.styleDefault(theme, parameters),
      lineStyle: 'mediumdashdot',
      fillPattern: 'X4',
    };
  },
  style11(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return {
      ...graphDataStyle.styleDefault(theme, parameters),
      lineStyle: 'shortdash',
      fillPattern: 'L3',
    };
  },
};

const deriveDataStyle = (
  theme: ThemeParameters,
  parameters: RuntimeThemeParameters,
  color: string
) => {
  switch (theme.baseThemeName) {
    case 'dark': {
      const altColor = increaseLumaDelta(color, graphWallFill(theme, parameters).fillColor, 32);
      return {
        color,
        fillColor: color,
        altColor,
        markerColor: altColor,
        lineColor: altColor,
        textColor: altColor,
      };
    }
    case 'high-contrast': {
      const altColor = increaseLumaDelta(
        color,
        graphWallFill(theme, parameters).fillColor,
        12,
        true
      );
      return {
        color,
        fillColor: color,
        altColor,
        markerColor: altColor,
        lineColor: altColor,
        textColor: altColor,
      };
    }
    default: {
      const altColor = increaseLumaDelta(color, graphWallFill(theme, parameters).fillColor, 22);
      return {
        color,
        fillColor: color,
        altColor,
        markerColor: altColor,
        lineColor: altColor,
        textColor: increaseLumaDelta(color, graphWallFill(theme, parameters).fillColor, 32),
      };
    }
  }
};
